
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Capteur, CapteursService } from '../../../services/capteurs.service';
import { Cartouche, CartouchesService } from '../../../services/cartouches.service';
import { StatutCapteursService, StatutCapteur } from '../../../services/statut-capteurs.service';

@Component({
  selector: 'app-capteurs',
  imports: [CommonModule, FormsModule],
  templateUrl: './capteurs.component.html',
  styleUrl: './capteurs.component.scss'
})
export class CapteursComponent implements OnInit {
  protected capteurs: Capteur[] = [];
  protected filter = '';
  protected page = 1;
  protected readonly pageSize = 10;
  protected loading = false;
  protected error = '';
  protected success = '';
  protected deletingId: number | null = null;
  protected cartouches: Cartouche[] = [];
  protected cartoucheNomCache: { [id: number]: string } = {};
  protected statutsCapteurs: StatutCapteur[] = [];
  protected statutNomCache: { [id: number]: string } = {};

  constructor(
    private readonly capteurService: CapteursService,
    private readonly cartouchesService: CartouchesService,
    private readonly statutCapteursService: StatutCapteursService,
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadCartouches();
    this.loadCapteurs();
    this.loadStatutsCapteurs();

    setTimeout(() => {
      if (this.loading) {
        this.loadCapteurs();
      }
    }, 2500);
  }

  private loadStatutsCapteurs(): void {
    this.statutCapteursService.list().subscribe({
      next: (statuts) => {
        this.statutsCapteurs = statuts;
        for (const s of statuts) {
          this.statutNomCache[s.id] = s.nom;
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.statutsCapteurs = [];
        this.statutNomCache = {};
        this.cdr.markForCheck();
      }
    });
  }

  public getStatutNom(id: number | null | undefined): string {
    if (!id) return '';
    return this.statutNomCache[id] || '';
  }

  protected isStatutEnCoursDeTransit(statutId: number | null | undefined): boolean {
    const nom = this.getStatutNom(statutId)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
    return nom === 'en cours de transit';
  }
  protected getCartoucheNom(id: number | null | undefined): string {
    // Cette méthode n'est plus utilisée, remplacée par getNomCartoucheAsync
    return '';
  }

  protected getNomCartoucheAsync(id: number | null | undefined, callback: (nom: string) => void): void {
    if (!id) {
      callback('');
      return;
    }
    this.cartouchesService.getNom(id).subscribe({
      next: (res) => callback(res.nom),
      error: () => callback('')
    });
  }
  private loadCartouches(): void {
    this.cartouches = [];
    this.cartoucheNomCache = {};
  }

  public fetchCartoucheNom(id: number | null | undefined): string {
    if (!id) return '';
    if (this.cartoucheNomCache[id]) return this.cartoucheNomCache[id];
    this.cartouchesService.getNom(id).subscribe({
      next: (res) => {
        this.cartoucheNomCache[id] = res.nom;
        this.cdr.markForCheck();
      },
      error: () => {
        this.cartoucheNomCache[id] = '';
        this.cdr.markForCheck();
      }
    });
    return '';
  }

  protected get filteredCapteurs(): Capteur[] {
    const q = this.normalizeText(this.filter);

    if (!q) {
      return this.capteurs;
    }

    return this.capteurs.filter((c) => {
      const cartoucheANom = c.cartouche_a ? (this.cartoucheNomCache[c.cartouche_a] ?? '') : '';
      const cartoucheBNom = c.cartouche_b ? (this.cartoucheNomCache[c.cartouche_b] ?? '') : '';
      const text = [
        c.numero_serie,
        c.commentaire,
        c.reference_envoi,
        c.code_bon_retour,
        cartoucheANom,
        cartoucheBNom,
        String(c.numero_nomenclature ?? ''),
      ]
        .filter((v): v is string => !!v)
        .join(' ')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
      return text.includes(q);
    });
  }

  protected get paginatedCapteurs(): Capteur[] {
    const filtered = this.filteredCapteurs;
    const start = (this.page - 1) * this.pageSize;
    return filtered.slice(start, start + this.pageSize);
  }

  protected get totalPages(): number {
    return Math.ceil(this.filteredCapteurs.length / this.pageSize);
  }

  protected get pageStartIndex(): number {
    if (this.filteredCapteurs.length === 0) {
      return 0;
    }
    return (this.page - 1) * this.pageSize + 1;
  }

  protected get pageEndIndex(): number {
    return Math.min(this.page * this.pageSize, this.filteredCapteurs.length);
  }

  protected onFilterChange(): void {
    this.page = 1;
    this.ensurePageWithinBounds();
  }

  protected previousPage(): void {
    if (this.page > 1) {
      this.page--;
    }
  }

  protected nextPage(): void {
    if (this.page < this.totalPages) {
      this.page++;
    }
  }

  protected refresh(): void {
    this.loadCapteurs();
  }

  protected editCapteur(capteur: Capteur): void {
    this.router.navigate(['/intranet/gestion-stocks/capteurs/modifier'], {
      queryParams: { capteurId: capteur.id },
    });
  }

  protected deleteCapteur(capteur: Capteur): void {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le capteur "${capteur.numero_serie}"?`)) {
      return;
    }

    this.deletingId = capteur.id;

    this.zone.run(() => {
      this.capteurService.delete(capteur.id).subscribe({
        next: () => {
          this.success = 'Capteur supprimé avec succès.';
          this.loadCapteurs();
          this.deletingId = null;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la suppression: ${this.getErrorMessage(err)}`;
          this.deletingId = null;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private normalizeText(text: string): string {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  private preloadCartoucheNames(capteurs: Capteur[]): void {
    const ids = new Set<number>();

    for (const capteur of capteurs) {
      if (capteur.cartouche_a) {
        ids.add(capteur.cartouche_a);
      }
      if (capteur.cartouche_b) {
        ids.add(capteur.cartouche_b);
      }
    }

    for (const id of ids) {
      if (this.cartoucheNomCache[id] !== undefined) {
        continue;
      }

      this.cartouchesService.getNom(id).subscribe({
        next: (res: { nom: string }) => {
          this.cartoucheNomCache[id] = res.nom;
          this.cdr.markForCheck();
        },
        error: () => {
          this.cartoucheNomCache[id] = '';
          this.cdr.markForCheck();
        }
      });
    }
  }

  private loadCapteurs(): void {
    this.loading = true;
    this.error = '';
    this.success = '';

    this.zone.run(() => {
      this.capteurService.list().subscribe({
        next: (capteurs: Capteur[]) => {
          this.capteurs = capteurs;
          this.preloadCartoucheNames(capteurs);
          this.ensurePageWithinBounds();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors du chargement: ${this.getErrorMessage(err)}`;
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    if (typeof err.error === 'string' && err.error.trim()) {
      return err.error;
    }

    if (err.error?.detail) {
      return err.error.detail;
    }

    if (err.error?.message) {
      return err.error.message;
    }

    return err.statusText || 'Erreur inconnue';
  }

  private ensurePageWithinBounds(): void {
    const pages = this.totalPages;
    if (pages === 0) {
      this.page = 1;
      return;
    }
    if (this.page > pages) {
      this.page = pages;
    }
    if (this.page < 1) {
      this.page = 1;
    }
  }
}
